import { useState } from 'react';

import {
  FreshStrawFrozenEmbryo,
  useLazySearchFreshStrawOnFarmEmbryosQuery,
} from '@/app/services/freshStrawRest';
import { Button } from '@/components/ui/button';

import type { FreshStrawSession } from '../../hooks/useFreshStrawSession';

interface FreshStrawOnFarmTogTabProps {
  session: FreshStrawSession;
}

export function FreshStrawOnFarmTogTab({
  session,
}: FreshStrawOnFarmTogTabProps) {
  const [caneSearch, setCaneSearch] = useState('');
  const [trigger, result] = useLazySearchFreshStrawOnFarmEmbryosQuery();

  const rows = (result.data ?? []) as FreshStrawFrozenEmbryo[];

  const handleSearch = () => {
    if (!caneSearch.trim()) return;
    trigger({
      caneSearch: caneSearch.trim(),
      accountId: session.accountId || undefined,
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <form
        className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-white p-3"
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch();
        }}
      >
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">
            Cane search
          </label>
          <input
            type="text"
            value={caneSearch}
            onChange={(e) => setCaneSearch(e.target.value)}
            placeholder="partial cane number"
            className="rounded border border-gray-300 px-2 py-1 text-sm"
            autoFocus
          />
        </div>
        <Button
          type="submit"
          disabled={!caneSearch.trim() || result.isFetching}
        >
          {result.isFetching ? 'Searching…' : 'Search'}
        </Button>
        {session.accountId && (
          <div className="text-xs text-gray-500">
            Filtered by current account.
          </div>
        )}
      </form>

      {result.isError && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Search failed.
        </div>
      )}

      {result.isSuccess && rows.length === 0 && (
        <div className="flex flex-1 items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-500">
          No on-farm frozen embryos match that search.
        </div>
      )}

      {rows.length > 0 && (
        <div className="min-h-0 flex-1 overflow-auto rounded border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-100 text-left text-xs font-semibold text-gray-600">
              <tr>
                <th className="px-2 py-1">Mating</th>
                <th className="px-2 py-1">Ranch</th>
                <th className="px-2 py-1">Recip</th>
                <th className="px-2 py-1">Cane</th>
                <th className="px-2 py-1">Straw</th>
                <th className="px-2 py-1">Tag</th>
                <th className="px-2 py-1">Stage</th>
                <th className="px-2 py-1">Grade</th>
                <th className="px-2 py-1">Partners</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.embryoId}
                  className="border-t border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-2 py-1">{r.mating ?? ''}</td>
                  <td className="px-2 py-1">{r.ranchCode ?? ''}</td>
                  <td className="px-2 py-1">{r.recipName ?? ''}</td>
                  <td className="px-2 py-1">{r.cane ?? ''}</td>
                  <td className="px-2 py-1">{r.strawNumber ?? ''}</td>
                  <td className="px-2 py-1">{r.tagNumber ?? ''}</td>
                  <td className="px-2 py-1">{r.stage ?? ''}</td>
                  <td className="px-2 py-1">{r.grade ?? ''}</td>
                  <td className="px-2 py-1">{r.partnerCount ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
